import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import { SellOffer, TradeSellOffer, SellOfferState, Trade, TradeState } from './types';
import { RootState } from '../types';
import tradingService from '../../services/tradingService';
import store from '@/store';
import _ from 'lodash';

const state: SellOfferState = {
  sellOffer: null,
  trades: [],
  createdSellOffer: null,
  errorCreatingSellOffer: null,
  errorCreatingTradeSellOffer: null,
  errorLoadingSellOffer: null,
  createdTradeSellOffer: false,
  verifiedTrades: null,
};

const mutations: MutationTree<SellOfferState> = {
  SET_SELL_OFFER(state: SellOfferState, sellOffer: SellOffer) {
    state.sellOffer = sellOffer;
  },

  SET_TRADES(state: SellOfferState, trades: Trade[]) {
    state.trades = trades;
  },

  SET_CREATED_SELL_OFFER(state: SellOfferState, sellOffer: SellOffer) {
    state.createdSellOffer = sellOffer;
  },

  SET_VERIFIED_TRADES(state: SellOfferState, verifiedTrades: Trade[]) {
    state.verifiedTrades = verifiedTrades;
  },

  SET_CREATED_TRADE_SELL_OFFER(state: SellOfferState, isCreated: boolean) {
    state.createdTradeSellOffer = isCreated;
  },

  SET_ERROR_CREATING_SELL_OFFER(state: SellOfferState, error: string) {
    state.errorCreatingSellOffer = error;
  },

  SET_ERROR_LOADING_SELL_OFFER(state: SellOfferState, error: string) {
    state.errorLoadingSellOffer = error;
  },

  SET_ERROR_CREATING_TRADE_SELL_OFFER(state: SellOfferState, error: string) {
    state.errorCreatingTradeSellOffer = error;
  },
};

const getters: GetterTree<SellOfferState, RootState> = {
  sellOffer: (state: SellOfferState) => state.sellOffer,
  trades: (state: SellOfferState) => state.trades,
  errorLoadingSellOffer: (state: SellOfferState) => state.errorLoadingSellOffer,
  errorCreatingSellOffer: (state: SellOfferState) => state.errorCreatingSellOffer,
  createdSellOffer: (state: SellOfferState) => state.createdSellOffer,
  createdTradeSellOffer: (state: SellOfferState) => state.createdTradeSellOffer,
  verifiedTrades: (state: SellOfferState) => state.verifiedTrades,
};

const actions: ActionTree<SellOfferState, RootState> = {
  async fetchSellOffer({ commit }: { commit: Function, rootState: any }, assetId: string) {
    store.dispatch('appState/startLoader');

    try {
      const sellOfferResponse = await tradingService.getSellOfferById(assetId);
      commit('SET_SELL_OFFER', sellOfferResponse.data);
      store.dispatch('appState/stopLoader');

      return sellOfferResponse;
    } catch (error) {
      store.dispatch('appState/stopLoader');
      commit('SET_SELL_OFFER', null);
      commit('SET_ERROR_LOADING_SELL_OFFER', error);

      return Promise.reject(error);
    }
  },
  async fetchTrades({ commit }: { commit: Function, rootState: any }, state?: TradeState) {
    store.dispatch('appState/startLoader');

    try {
      let tradesResponse;
      switch (state) {
        case TradeState.Active:
          tradesResponse = await tradingService.getActiveTrades();
          break;
        case TradeState.Pending:
          tradesResponse = await tradingService.getPendingTrades();
          break;
        default:
          tradesResponse = await tradingService.getTrades();
          break;
      }

      store.dispatch('appState/stopLoader');
      commit('SET_TRADES', tradesResponse.data);

      return tradesResponse;
    } catch (error) {
      store.dispatch('appState/stopLoader');
      commit('SET_TRADES', []);

      return Promise.reject(error);
    }
  },
  async createSellOffer({ commit }: { commit: Function, rootState: any }, sellOffer: SellOffer) {
    store.dispatch('appState/startLoader');
    commit('SET_ERROR_CREATING_SELL_OFFER', null);

    try {
      const sellOfferResponse = await tradingService.createSellOffer(sellOffer);
      commit('SET_CREATED_SELL_OFFER', sellOfferResponse.data);
      store.dispatch('appState/stopLoader');

      return sellOfferResponse;
    } catch (error) {
      store.dispatch('appState/stopLoader');
      commit('SET_CREATED_SELL_OFFER', undefined);
      commit('SET_ERROR_CREATING_SELL_OFFER', _.get(error, 'response.data.error') || error);

      return Promise.reject(error);
    }
  },

  async createTradeSellOffer({ commit }: { commit: Function, rootState: any }, { id, tradeSellOffer }) {
    store.dispatch('appState/startLoader');
    commit('SET_ERROR_CREATING_TRADE_SELL_OFFER', null);

    try {
      const tradeSellOfferResponse = await tradingService.createTradeSellOffer(id, tradeSellOffer);
      commit('SET_CREATED_TRADE_SELL_OFFER', tradeSellOfferResponse.data);
      store.dispatch('appState/stopLoader');

      return tradeSellOfferResponse;
    } catch (error) {
      store.dispatch('appState/stopLoader');
      commit('SET_CREATED_TRADE_SELL_OFFER', undefined);
      commit('SET_ERROR_CREATING_TRADE_SELL_OFFER', _.get(error, 'response.data.error') || error);

      return Promise.reject(error);
    }
  },

};

export const sellOffer: Module<SellOfferState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
